/**
 * THE BAND DIMENSIONS: what price did against the PTA/PTB channels it was given.
 *
 * Six of Chart Analytica's fifteen, all driven by one ladder and one pass over the bars:
 *
 * <pre>
 *    2  excursion depth per SECTION      1x / 2x / 3x / 3x+, long and short
 *    3  equator penetrations             a close CROSSING the midline, by approach
 *    4  equator reversions               a touch of the midline REJECTED, by approach
 *    8  band height vs excursion         three correlations, and only one is readable
 *   11  boundary penetrations b0..b3     the EVENT count per layer
 *   12  boundary reversions  b0..b3      the EVENT count per layer
 * </pre>
 *
 * <h2>A PENETRATION is a TRANSITION, never a state</h2>
 *
 * `prev.close` at or inside the level, `close` strictly beyond it. Read as a state — "the close is
 * beyond b1" — a single excursion that stayed out for forty sessions would be counted forty times,
 * and the layer with the longest trends would look like the layer price penetrates most often.
 * This repository has already paid for that reading once, in the containment field, where
 * continuation had to be made to fire nothing.
 *
 * <h2>A REVERSION is a TOUCH that was REJECTED</h2>
 *
 * The previous close inside, the bar's own EXTREME reaching the level, and the close back inside.
 * The extreme rather than the close is what makes it a touch at all: a bar that never traded at
 * the line did not test it. The previous close inside is what makes it a rejection rather than a
 * re-entry from outside, which is a different event and belongs to the layer it came back through.
 *
 * <h2>Penetration and reversion are MUTUALLY EXCLUSIVE on one bar and one layer</h2>
 *
 * Both require the previous close inside, and they disagree about this one — beyond it, or back
 * inside it. A close exactly ON the level is NEITHER: it has not gone through and it has not been
 * rejected. That asymmetry is deliberate and is the same one `guideLevels`' own consumers use.
 */

import {
  BAR_CLOSE, BAR_HIGH, BAR_LOW, BOUNDARY_KEYS, emptyBoundaryTally, heightsFromEquator,
} from './analyticaGeometry';
import { bucketIndex, mean, median, pearson, rate } from './analyticaStats';

/** Excursion tiers, in band heights beyond the measured edge. `bucketIndex` edges are exclusive. */
export const EXCURSION_EDGES = [1, 2, 3];
export const EXCURSION_LABELS = ['under 1x', '1x', '2x', '3x+'];

const n = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/**
 * Walks every bar of every section once and tallies the six dimensions.
 *
 * <p>ONE pass, because the dimensions all ask about the same bar against the same ladder and a
 * pass apiece would be six reads of an 8,000-bar series for no gain. The per-section accumulators
 * are what let dimension 2 (a per-section maximum) share the walk with dimension 11 (a per-bar
 * event count).
 *
 * @param {Array<object>} sections the output of `sectionsFrom`
 * @param {Array<number[]>} ohlc the chart's own bars, ECharts order
 * @returns {object} the six dimensions, plus the per-section rows the dashboard tabulates
 */
export function analyseBands(sections, ohlc) {
  const list = Array.isArray(sections) ? sections : [];
  const bars = Array.isArray(ohlc) ? ohlc : [];

  const penetrations = { up: emptyBoundaryTally(), down: emptyBoundaryTally() };
  const reversions = { up: emptyBoundaryTally(), down: emptyBoundaryTally() };
  const equator = {
    penetrationUp: 0, penetrationDown: 0, reversionFromAbove: 0, reversionFromBelow: 0,
    touches: 0,
  };
  const rows = [];
  /*
   * EVERY EVENT, with the bar it happened on.
   *
   * <p>Two consumers, which is why it is collected rather than only counted: the chart overlay
   * marks these bars, and the volume dimension asks what volume did on them. Counting them here
   * and re-deriving the bar indices in either of those would be a second walk that could
   * disagree with this one about what an event is — and the disagreement would be invisible,
   * because both answers are plausible counts.
   */
  const events = [];

  list.forEach((section) => {
    const { from, to, h, top, bottom, up, down } = section;
    let maxHigh = -Infinity;
    let minLow = Infinity;
    let reachedUp = 0;    // deepest layer the UP side penetrated, in heights
    let reachedDown = 0;

    for (let i = from; i <= to; i++) {
      const bar = bars[i];
      const prev = bars[i - 1];
      if (!bar || !prev) continue;
      const close = n(bar[BAR_CLOSE]);
      const high = n(bar[BAR_HIGH]);
      const low = n(bar[BAR_LOW]);
      const prevClose = n(prev[BAR_CLOSE]);
      if (close === null || high === null || low === null || prevClose === null) continue;

      if (high > maxHigh) maxHigh = high;
      if (low < minLow) minLow = low;

      // ── the four boundary layers, both sides ──────────────────────────────────────────────
      for (let b = 0; b < BOUNDARY_KEYS.length; b++) {
        const key = BOUNDARY_KEYS[b];
        const lvlUp = n(up[b]);
        const lvlDown = n(down[b]);

        if (lvlUp !== null && prevClose <= lvlUp) {
          if (close > lvlUp) {
            penetrations.up[key] += 1;
            events.push({ index: i, kind: 'penetration', side: 'up', layer: key,
              level: lvlUp, category: section.category });
          } else if (high >= lvlUp && close < lvlUp) {
            reversions.up[key] += 1;
            events.push({ index: i, kind: 'reversion', side: 'up', layer: key,
              level: lvlUp, category: section.category });
          }
        }
        if (lvlDown !== null && prevClose >= lvlDown) {
          if (close < lvlDown) {
            penetrations.down[key] += 1;
            events.push({ index: i, kind: 'penetration', side: 'down', layer: key,
              level: lvlDown, category: section.category });
          } else if (low <= lvlDown && close > lvlDown) {
            reversions.down[key] += 1;
            events.push({ index: i, kind: 'reversion', side: 'down', layer: key,
              level: lvlDown, category: section.category });
          }
        }
      }

      // ── the equator, which is the one level INSIDE the channel ────────────────────────────
      //
      // Its two directions are named by where price came FROM, not by which way it ended up,
      // because that is what a reader watching the midline is tracking: a close arriving from
      // below and finishing above is an upward penetration whatever it does next.
      const eq = n(section.equator);
      if (eq !== null) {
        const touched = low <= eq && high >= eq;
        if (touched) equator.touches += 1;
        const mark = (kind, side) => events.push({ index: i, kind, side, layer: 'equator',
          level: eq, category: section.category });
        if (prevClose <= eq && close > eq) { equator.penetrationUp += 1; mark('penetration', 'up'); }
        else if (prevClose >= eq && close < eq) {
          equator.penetrationDown += 1; mark('penetration', 'down');
        } else if (prevClose > eq && low <= eq && close > eq) {
          equator.reversionFromAbove += 1; mark('reversion', 'up');
        } else if (prevClose < eq && high >= eq && close < eq) {
          equator.reversionFromBelow += 1; mark('reversion', 'down');
        }
      }
    }

    // ── dimension 2: how far this SECTION got, each way ───────────────────────────────────
    if (Number.isFinite(maxHigh) && Number.isFinite(minLow) && h > 0) {
      reachedUp = Math.max(0, (maxHigh - top) / h);
      reachedDown = Math.max(0, (bottom - minLow) / h);
      rows.push({
        category: section.category,
        date1: section.date1,
        from,
        to,
        bars: to - from + 1,
        clipped: section.clipped,
        height: h,
        // Height as a FRACTION of the level it sits at, which is the only form comparable across
        // three decades of price. A 0.91-point channel in 1993 and a 15-point one in 2026 are the
        // same channel in every sense a reader cares about.
        heightPct: section.equator > 0 ? h / section.equator : null,
        top,
        bottom,
        equator: section.equator,
        maxHigh,
        minLow,
        reachedUp,
        reachedDown,
        // The furthest price travelled from the midline, either way, in heights. Dimension 8's
        // dependent variable, and the natural scale-free measure of "how big was the move".
        maxFromEquator: Math.max(
          Math.abs(heightsFromEquator(maxHigh, section) || 0),
          Math.abs(heightsFromEquator(minLow, section) || 0),
        ),
        maxFromEquatorPrice: Math.max(
          Math.abs(maxHigh - section.equator), Math.abs(minLow - section.equator),
        ),
        upTier: EXCURSION_LABELS[bucketIndex(reachedUp, EXCURSION_EDGES)],
        downTier: EXCURSION_LABELS[bucketIndex(reachedDown, EXCURSION_EDGES)],
      });
    }
  });

  return {
    sections: rows.length,
    clippedSections: rows.filter(r => r.clipped).length,
    excursion: excursionTiers(rows),
    equator: {
      ...equator,
      // The denominator for a midline rate is the bars that TOUCHED it, not every bar of the
      // scan: a bar fifty points away neither penetrated nor reverted and has no opinion.
      penetrationRate: rate(equator.penetrationUp + equator.penetrationDown, equator.touches),
      reversionRate: rate(equator.reversionFromAbove + equator.reversionFromBelow, equator.touches),
    },
    boundaries: boundaryBreakdown(penetrations, reversions, rows),
    heightVsExcursion: heightCorrelation(rows),
    rows,
    events: events.sort((a, b) => a.index - b.index),
  };
}

/**
 * Dimension 2 — how many sections reached each tier, each way.
 *
 * <p>Counted per SECTION rather than per event, so a channel that spent a month above b2 counts
 * once. The frequency is of the sections that exist, which is what "frequency of sections
 * achieving a 2x move" asks.
 */
function excursionTiers(rows) {
  const blank = () => EXCURSION_LABELS.reduce((a, k) => { a[k] = 0; return a; }, {});
  const long = blank();
  const short = blank();
  rows.forEach((r) => { long[r.upTier] += 1; short[r.downTier] += 1; });

  const asRates = (tally) => EXCURSION_LABELS.map(label => ({
    label,
    count: tally[label],
    ...rate(tally[label], rows.length),
  }));

  return {
    total: rows.length,
    long: asRates(long),
    short: asRates(short),
    // "Achieved at least 1x" is the reading most people mean by an excursion, and it is not any
    // single tier -- it is every tier but the first. Stated, rather than left to be summed.
    longAtLeast1x: rate(rows.filter(r => r.reachedUp >= 1).length, rows.length),
    shortAtLeast1x: rate(rows.filter(r => r.reachedDown >= 1).length, rows.length),
    medianLong: median(rows.map(r => r.reachedUp)),
    medianShort: median(rows.map(r => r.reachedDown)),
  };
}

/**
 * Dimensions 11 and 12 — the event counts per layer, with the rate each layer SURVIVES.
 *
 * <p>The survival rate is the number worth reading and it is not in the brief: of the bars that
 * tested a layer, how many were rejected rather than going through. Penetrations and reversions
 * are mutually exclusive on one bar and one layer, so they sum to the tests and the ratio is
 * well defined — which is exactly why both halves were asked for.
 */
function boundaryBreakdown(penetrations, reversions, rows) {
  const side = (which) => BOUNDARY_KEYS.map((key) => {
    const pen = penetrations[which][key];
    const rev = reversions[which][key];
    return {
      layer: key,
      penetrations: pen,
      reversions: rev,
      tests: pen + rev,
      // The HOLD rate: how often this layer turned price back when price came to it.
      hold: rate(rev, pen + rev),
    };
  });
  return {
    sections: rows.length,
    up: side('up'),
    down: side('down'),
  };
}

/**
 * Dimension 8 — and the whole of it is WHICH correlation is readable.
 *
 * <pre>
 *   absolute    r(h, excursion in PRICE)      DOMINATED BY PRICE LEVEL on a long chart
 *   relative    r(h/eq, excursion/eq)         the one that controls for it -- the headline
 *   normalised  r(h, excursion in HEIGHTS)    NEGATIVE BY CONSTRUCTION if nothing is there
 * </pre>
 *
 * <p>All three are reported because each alone misleads, and in a different direction.
 *
 * <p><b>absolute</b> is the obvious reading and is close to useless over three decades: SPY's
 * channels and its moves both grew with its price from 45 to 780, so the two series share a
 * trend and r comes back large and positive whether or not a taller band means anything.
 *
 * <p><b>normalised</b> divides the move by the very quantity it is being correlated against. If
 * the move in price were independent of the band height, `excursion/h` would correlate strongly
 * NEGATIVELY with h as a matter of arithmetic — so a negative r there is the null expectation and
 * not a finding. A value near zero or above is the interesting one.
 *
 * <p><b>relative</b> expresses both sides as a fraction of the level they sit at, so neither
 * carries the price trend and neither is a function of the other. It is the one to read.
 */
function heightCorrelation(rows) {
  const withEq = rows.filter(r => n(r.heightPct) !== null && r.equator > 0);
  const absolute = pearson(rows.map(r => r.height), rows.map(r => r.maxFromEquatorPrice));
  const relative = pearson(
    withEq.map(r => r.heightPct),
    withEq.map(r => r.maxFromEquatorPrice / r.equator),
  );
  const normalised = pearson(rows.map(r => r.height), rows.map(r => r.maxFromEquator));

  return {
    absolute,
    relative,
    normalised,
    quartiles: heightQuartiles(withEq),
  };
}

/**
 * The "distribution mapping" half of dimension 8.
 *
 * <p>Bands sorted by RELATIVE height and split into four groups, each reporting the mean excursion
 * in band heights. Within a quartile the height is roughly constant, so the arithmetic bias that
 * makes the normalised correlation unreadable is largely held still — which makes this the
 * cross-check on it rather than a restatement.
 *
 * <p>Fewer than eight bands yields nothing: two per quartile is not a distribution.
 */
function heightQuartiles(rows) {
  if (rows.length < 8) return [];
  const sorted = [...rows].sort((a, b) => a.heightPct - b.heightPct);
  const size = Math.floor(sorted.length / 4);
  const out = [];
  for (let q = 0; q < 4; q++) {
    const slice = q === 3 ? sorted.slice(q * size) : sorted.slice(q * size, (q + 1) * size);
    out.push({
      quartile: q + 1,
      n: slice.length,
      medianHeightPct: median(slice.map(r => r.heightPct)),
      meanExcursionHeights: mean(slice.map(r => r.maxFromEquator)),
      meanExcursionPct: mean(slice.map(r => r.maxFromEquatorPrice / r.equator)),
    });
  }
  return out;
}

export default analyseBands;
